import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Role, ServiceCenter, Traininggroup} from "../../../../../types/domain";
import {MantineReactTable, MRT_ColumnDef, useMantineReactTable} from "mantine-react-table";
import {ActionIcon, Alert, Box, Button, Paper, Tooltip} from "@mantine/core";
import {ModalType} from "../../../../../types/UITypes";
import CustomModal from "../modal/CustomModal";
import TraininggroupService from "../../../../../services/traininggroup.service";
import {IconArchive, IconPencil} from "@tabler/icons-react";
import {MRT_Localization_DE} from "mantine-react-table/locales/de";
import {GroupModal} from "../modal/GroupModal";
import ParticipantService from "../../../../../services/participant.service";
import * as Sentry from "@sentry/react";

type UserData = {
    userRole: Role | null,
    userServiceCenters: Array<ServiceCenter>
}
type GroupTableProps = {
    userData: UserData,
    tabValue: string
}

export function GroupTable({ userData: { userRole, userServiceCenters }, tabValue }: GroupTableProps) {
    const [data, setData] = useState<Traininggroup[]>([]);
    const [showAlert, setShowAlert] = useState(false);
    const [showGroupModal, setShowGroupModal] = useState<boolean>(false);
    const [groupToEdit, setGroupToEdit] = useState<Traininggroup | null>(null);
    const [modal, setModal] = useState<ModalType>({
        show: false,
        title: "",
        body: "",
        decline: false,
        handleOk: (() => {
        }),
        handleCancel: (() => {
        })
    })

    //definition of table columns
    const columns = useMemo<MRT_ColumnDef<Traininggroup>[]>(
        () => [
            {
                accessorKey: 'name',
                header: 'Gruppenname'
            },
            {
                id: 'servicecenter',
                header: 'Servicestelle',
                accessorFn: (row) => row.serviceCenter?.name
            },
            {
                id: 'ParticipantCount',
                header: 'Anzahl Gruppenzugehöriger',
                accessorFn: (row) => row.participants.length
            }
        ], [tabValue]
    );

    const fetchData = useCallback(async () => {
        if (tabValue !== "groups" || !userRole) return;
        try {
            const response = await TraininggroupService.getAll();
            let groups = response.data.filter(g => g.active);
            if (userRole?.name === 'Admin') {
                setData(groups);
            } else if (userRole?.name === 'DSS Leiter*in') {
                setData(groups.filter(g =>
                    userServiceCenters.some(sc => sc.id === (g.serviceCenter?.id))
                ));
            }
        } catch (e) {
            console.error(e);
        }
    }, [userRole, userServiceCenters, tabValue]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    /**
     * Closes group modal and fetches data
     */
    const handleCloseGroupModal = useCallback(async () => {
        setShowGroupModal(false);
        setGroupToEdit(null);
        await fetchData();
    }, [fetchData]);

    /**
     * Opens a modal to update a traininggroup
     * @param group with new data to update
     */
    function updateGroup(group: Traininggroup) {
        setGroupToEdit(group);
        setShowGroupModal(true);
    }

    /**
     * Opens a modal to create a traininggroup
     */
    function createGroup() {
        setGroupToEdit(null);
        setShowGroupModal(true);
    }

    /**
     * Sets a group to inactive in the DB
     * @param group to inactivate
     */
    async function inactivateGroup(group: Traininggroup) {
        setModal({
            ...modal,
            show: true,
            title: "Hinweis",
            body: "Soll der Eintrag wirklich archiviert werden? Teilnehmer*innen bleiben erhalten!",
            decline: true,
            handleOk: async () => {
                group.active = false;
                await TraininggroupService.update(group, group.id)
                    .catch((e: Error) => {
                        Sentry.captureException(e);
                        setShowAlert(true);
                        return;
                    });

                group.participants.forEach(p => {
                    p.group = null;
                    p.serviceCenter = group.serviceCenter;
                    ParticipantService.update(p, p.id) //Remove participants from group
                        .catch((e: Error) => {
                            Sentry.captureException(e);
                            setShowAlert(true);
                            return;
                        });
                });

                setModal({...modal, show: false})
                await fetchData();
            },
            handleCancel: () => {
                setModal({...modal, show: false})
                fetchData();
            }
        })
    }

    const table = useMantineReactTable<Traininggroup>({
        columns,
        data,
        initialState: {
            pagination: {
                pageSize: 10,
                pageIndex: 0
            },
            sorting: [
                {
                    id: 'name',
                    desc: false
                }
            ]
        },
        enableColumnActions: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        positionPagination: "bottom",
        enableRowActions: true,
        positionActionsColumn: "last",
        renderRowActions: ({row}) => {
            return <Box sx={{display: 'flex', flexWrap: 'nowrap', gap: '8px'}}>
                <Tooltip label="Bearbeiten">
                    <ActionIcon color="blueish" onClick={() => updateGroup(row.original)}>
                        <IconPencil/>
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Archivieren">
                    <ActionIcon style={{color: "var(--salmon)"}} onClick={() => inactivateGroup(row.original)}>
                        <IconArchive/>
                    </ActionIcon>
                </Tooltip>
            </Box>
        },
        localization: MRT_Localization_DE,
        mantinePaginationProps: {
            showRowsPerPage: false
        }
    });
    return (
        <>
            <Paper radius="xs" style={{marginTop: "1rem"}}>
                <MantineReactTable
                    table={table}
                />
                {
                    showAlert ?
                        <Alert id={'participantDatabaseAlert'} w={"100%"}
                               title={"Archivieren fehlgeschlagen"}
                               ta={"left"} color="red" mt={"10px"}>
                            Fehler beim Archivieren des Eintrages!
                        </Alert> : <></>
                }
                <div className="buttonRow">
                    <Button onClick={createGroup}>
                        Neuer Eintrag
                    </Button>
                </div>
            </Paper>
            {
                showGroupModal && (
                    <GroupModal
                        opened={showGroupModal}
                        close={handleCloseGroupModal}
                        title={groupToEdit ? "Gruppe aktualisieren" : "Gruppe anlegen"}
                        group={groupToEdit}
                        userRole={userRole}
                        userServiceCenters={userServiceCenters}
                    />
                )
            }
            <CustomModal title={modal.title}
                         body={modal.body}
                         decline={modal.decline} showModal={modal.show} handleOk={modal.handleOk}
                         handleCancel={modal.handleCancel}
            />
        </>
    );
}